import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const TESTIMONIALS = [
  {
    initials: "DA",
    quote:
      "It's wonderful that innovations like Birdseye exist because we, as a country, really need to start adopting Digital Farming.\"",
    name: "Data Analytics Head and Research Specialist",
    role: "BAI",
  },
  {
    initials: "BA",
    quote:
      "Litter monitoring is usually time-consuming, so a tool like Birdseye really makes things easier for farms.\"",
    name: "Director",
    role: "BAFS - Standards Development Division",
  },
  {
    initials: "CC",
    quote:
      "I think it would be a huge help to have a device that can detect it immediately and take action right away to address the wet litter problem.\"",
    name: "Center Chief",
    role: "BAI - NSPRDC",
  },
  {
    initials: "CS",
    quote:
      "This is helpful for small-scale establishments, especially because there are only a few technologies they can afford that could intervene with the problem.\"",
    name: "Chief Statistical Specialist",
    role: "PSA - LPSD",
  },
];

export default function SignUp() {
  const navigate = useNavigate();
  const [order, setOrder] = useState(TESTIMONIALS.map((_, i) => i));
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });
  const [activationCode, setActivationCode] = useState("");
  const [step, setStep] = useState("form");
  const [statusText, setStatusText] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState("");
  const [resendSuccess, setResendSuccess] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendHover, setResendHover] = useState(false);
  const apiBase = "https://sys.birdseye.work";
  const videoRef = useRef(null);
  const [videoLoopFade, setVideoLoopFade] = useState(false);

  const sendToBack = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setOrder((prev) => {
        const next = [...prev];
        const top = next.shift();
        next.push(top);
        return next;
      });
      setIsAnimating(false);
    }, 400);
  };

  const scheduleNext = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(sendToBack, 3800);
  };

  useEffect(() => {
    scheduleNext();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!video.duration) return;
      const progress = video.currentTime / video.duration;
      setVideoLoopFade(progress > 0.88);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timeout = setTimeout(() => setResendCountdown((current) => Math.max(0, current - 1)), 1000);
    return () => clearTimeout(timeout);
  }, [resendCountdown]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = form.phone.replace(/\D/g, "");

    if (step === "done") {
      navigate("/login");
      return;
    }

    setStatusText("");
    setLoading(true);

    if (step === "form") {
      try {
        const phoneQuery = encodeURIComponent(cleanPhone);
        const response = await fetch(
          `${apiBase}/auth/send_activation_code?phone_number=${phoneQuery}`,
          { method: "POST" }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to send activation code.");
        setStep("code");
        setStatusText("Activation code sent. Please check your phone.");
      } catch (err) {
        setStatusText(err.message);
      }
    } else if (step === "code") {
      try {
        const phoneQuery = encodeURIComponent(cleanPhone);
        const codeQuery = encodeURIComponent(activationCode);

        const checkResponse = await fetch(
          `${apiBase}/auth/check_activation_code?phone_number=${phoneQuery}&activation_code=${codeQuery}`,
          { method: "POST" }
        );
        const checkData = await checkResponse.json();
        if (!checkResponse.ok) throw new Error(checkData.message || "Invalid activation code.");

        const signupResponse = await fetch(`${apiBase}/auth/signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: `${form.firstName} ${form.lastName}`.trim(),
            email: form.email,
            password: form.password,
            activationCode,
          }),
        });
        const signupData = await signupResponse.json();
        if (!signupResponse.ok) throw new Error(signupData.message || "Signup failed.");

        setStep("done");
        setStatusText("Signup complete! You can now sign in.");
      } catch (err) {
        setStatusText(err.message);
      }
    }

    setLoading(false);
  };

  const handleResendActivation = async () => {
    if (resendLoading || resendCountdown > 0) return;
    setResendError("");
    setResendSuccess("");

    const cleanPhone = form.phone.replace(/\D/g, "");
    if (!cleanPhone) {
      setResendError("Please enter your phone number first.");
      return;
    }

    setResendLoading(true);

    try {
      const phoneQuery = encodeURIComponent(cleanPhone);
      const response = await fetch(
        `${apiBase}/auth/send_activation_code?phone_number=${phoneQuery}`,
        { method: "POST" }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to resend activation code.");
      setResendSuccess("Code resent!");
      setResendCountdown(30);
    } catch (err) {
      setResendError(err.message || "Could not resend code.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleManualShuffle = () => {
    sendToBack();
    scheduleNext();
  };

  return (
    <motion.div
      style={styles.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Sky */}
      <div style={styles.sky} />
      
      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        style={{
          ...styles.backgroundVideo,
          opacity: videoLoopFade ? 0.16 : 0.3,
          filter: videoLoopFade ? "blur(2px)" : "none",
        }}
      >
        <source src="/loopsign.mp4" type="video/mp4" />
      </video>
      
      <div style={{ ...styles.cloud, ...styles.c1 }} />
      <div style={{ ...styles.cloud, ...styles.c2 }} />
      <div style={{ ...styles.cloud, ...styles.c3 }} />

      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <button style={styles.btnGhost} onClick={() => navigate("/")}>
            <img src="/images/logo.png" alt="" style={{ height: 24 }} />
            <img src="/images/logo-text.png" alt="Birdseye" style={{ height: 20 }} />
          </button>
          <ul style={styles.navLinks}>
            {["Features", "How it works", "Pricing", "Contact us"].map((item) => (
              <li key={item} style={{ listStyle: "none" }}>
                <a href={`#${item.toLowerCase().replace(/ /g, "-")}`} style={styles.navLink}>
                  {item}
                </a>
              </li>
            ))}
          </ul>
          <div style={styles.navActions}>
            <button style={styles.btnGhost} onClick={() => navigate("/login")}>Log in</button>
            <button style={styles.btnNav} onClick={() => navigate("/signup")}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* Page */}
      <div style={styles.page}>
        <div style={styles.container}>

          {/* LEFT */}
          <div style={styles.left}>
            <motion.h1
              style={styles.h1}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
            >
              Discover how Birdseye fits into your farm, 
              <span style={styles.stepByStep}> step by step</span>.
            </motion.h1>

            <motion.p
              style={styles.subtitle}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
            >
              Share how your farm works day to day. We’ll show you where Birdseye fits in.
              
            </motion.p>

            {/* Stacked Card Deck */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
            >
              <div style={styles.deckOuter}>
                <div style={styles.deckWrap}>
                  {[...order].reverse().map((cardIdx, revIdx) => {
                    const total = Math.min(order.length, 3);
                    const pos = total - 1 - revIdx;
                    if (pos >= 3) return null;

                    const isTop = pos === 0;
                    const t = TESTIMONIALS[cardIdx];

                    const restY = pos * 12;
                    const restScale = 1 - pos * 0.045;
                    const restOpacity = 1 - pos * 0.2;
                    const exitOpacity = isTop && isAnimating ? 0 : restOpacity;

                    return (
                      <div
                        key={cardIdx}
                        onClick={isTop ? handleManualShuffle : undefined}
                        style={{
                          ...styles.card,
                          position: "absolute",
                          top: 0, left: 0, right: 0,
                          zIndex: 10 - pos,
                          transform: `translateY(${restY}px) scale(${restScale})`,
                          opacity: exitOpacity,
                          transition: "transform 0.4s cubic-bezier(.4,0,.2,1), opacity 0.4s ease",
                          transformOrigin: "bottom center",
                          cursor: isTop ? "pointer" : "default",
                          pointerEvents: isTop ? "auto" : "none",
                        }}
                      >
                        <p style={styles.cardQuote}>
                          <span style={styles.quoteMark}>"</span>
                          {t.quote}
                        </p>
                        <div style={styles.cardAuthor}>
                          <div style={styles.avatar}>
                            <svg viewBox="0 0 64 64" style={styles.avatarSvg} aria-hidden="true">
                              <circle cx="32" cy="32" r="30" fill="rgba(255,255,255,0.1)" />
                              <circle cx="32" cy="24" r="12" fill="rgba(255,255,255,0.85)" />
                              <path d="M16 52c0-11 9-20 20-20s20 9 20 20H16z" fill="rgba(255,255,255,0.85)" />
                            </svg>
                          </div>
                          <div>
                            <div style={styles.authorName}>{t.name}</div>
                            <div style={styles.authorRole}>{t.role}</div>
                          </div>
                        </div>
                        {isTop && (
                          <div style={styles.tapHint}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>

          {/* RIGHT – Glass Form */}
          {/* ↓ opacity + subtle y only — no x offset to avoid the snap glitch */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6, ease: "easeOut" }}
          >
            <div style={styles.formGlass}>
              <div style={styles.formTitle}>Create your account</div>
              <div style={styles.formSub}>
                Join our Birdseye farmers with a clear view of their operations.
              </div>

              <div style={styles.row}>
                <Field label="First name">
                  <input
                    name="firstName" type="text" placeholder="Jane"
                    value={form.firstName} onChange={handleChange}
                    style={styles.input} onFocus={applyFocus} onBlur={removeFocus}
                  />
                </Field>
                <Field label="Last name">
                  <input
                    name="lastName" type="text" placeholder="Douglas"
                    value={form.lastName} onChange={handleChange}
                    style={styles.input} onFocus={applyFocus} onBlur={removeFocus}
                  />
                </Field>
              </div>

              <Field label="Email address">
                <input
                  name="email" type="email" placeholder="you@company.com"
                  value={form.email} onChange={handleChange}
                  style={styles.input} onFocus={applyFocus} onBlur={removeFocus}
                />
              </Field>

              <Field label="Password">
                <input
                  name="password" type="password" placeholder="Create a password"
                  value={form.password} onChange={handleChange}
                  style={styles.input} onFocus={applyFocus} onBlur={removeFocus}
                />
              </Field>

              <Field label="Contact number">
                <input
                  name="phone" type="tel" placeholder="+639111111234"
                  value={form.phone} onChange={handleChange}
                  style={styles.input} onFocus={applyFocus} onBlur={removeFocus}
                />
              </Field>

              {step === "code" && (
                <>
                  <Field label="Activation code">
                    <input
                      name="activationCode" type="text" placeholder="123456"
                      value={activationCode} onChange={(e) => setActivationCode(e.target.value)}
                      style={styles.input} onFocus={applyFocus} onBlur={removeFocus}
                    />
                  </Field>

                  <div style={styles.resendRow}>
                    <button
                      type="button"
                      onClick={handleResendActivation}
                      onMouseEnter={() => setResendHover(true)}
                      onMouseLeave={() => setResendHover(false)}
                      disabled={resendLoading || resendCountdown > 0}
                      style={{
                        ...styles.resendLink,
                        opacity: resendLoading || resendCountdown > 0 ? 0.55 : 1,
                        cursor: resendLoading || resendCountdown > 0 ? "default" : "pointer",
                        color: resendLoading || resendCountdown > 0 ? "#ff6b6b" : (resendHover ? "#ff9fa8" : "#ff6b6b"),
                        textDecoration: resendHover ? "underline" : "none",
                      }}
                    >
                      {resendCountdown > 0
                        ? `Resend in ${resendCountdown}s`
                        : "Didn't receive a code? Resend"}
                    </button>
                    {(resendSuccess || resendError) && (
                      <span style={{
                        ...styles.resendNotice,
                        color: resendError ? "#ff9fa8" : "#9cf7c7",
                      }}>
                        {resendError || resendSuccess}
                      </span>
                    )}
                  </div>
                </>
              )}

              <button
                style={styles.btnSubmit}
                onClick={handleSubmit}
                disabled={loading}
              >
                {step === "form" && "Send activation code"}
                {step === "code" && "Verify activation code"}
                {step === "done" && "Go to Login"}
              </button>

              {statusText && <p style={styles.statusText}>{statusText}</p>}

              <p style={styles.formFooter}>
                By signing up, you agree to our{" "}
                <a href="#" style={styles.footerLink}>Terms of Service</a> and{" "}
                <a href="#" style={styles.footerLink}>Privacy Policy</a>.
              </p>

              <div style={styles.divider}>
                <div style={styles.dividerLine} />
                <span style={styles.dividerText}>Already have an account?</span>
                <div style={styles.dividerLine} />
              </div>

              <div style={styles.signInLink}>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); navigate("/login"); }}
                  style={styles.signInA}
                >
                  Sign in instead
                </a>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;1,400&family=Playfair+Display:ital,wght@1,400;1,500&display=swap');
        input::placeholder { color: rgba(255,255,255,0.28); }
        input:focus { outline: none; }
        a { cursor: pointer; }
        
        @keyframes videoFade {
          0% { opacity: 0.3; }
          85% { opacity: 0.3; }
          95% { opacity: 0.1; }
          100% { opacity: 0.3; }
        }
      `}</style>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function applyFocus(e) {
  e.target.style.borderColor = "rgba(255,37,37,0.5)";
  e.target.style.background = "rgba(255,255,255,0.11)";
  e.target.style.boxShadow = "0 0 0 3px rgba(255,37,37,0.1)";
}
function removeFocus(e) {
  e.target.style.borderColor = "rgba(255,255,255,0.12)";
  e.target.style.background = "rgba(255,255,255,0.08)";
  e.target.style.boxShadow = "none";
}

const styles = {
  root: {
    fontFamily: "'DM Sans', sans-serif",
    background: "#060a14",
    color: "#fff",
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
  },
  sky: {
    position: "fixed", inset: 0, zIndex: 0,
    background: `
      radial-gradient(ellipse 80% 60% at 20% 110%, rgba(30,60,120,0.7) 0%, transparent 60%),
      radial-gradient(ellipse 60% 50% at 80% -10%, rgba(15,35,90,0.8) 0%, transparent 55%),
      linear-gradient(175deg, #0a1628 0%, #0d1f44 35%, #112050 55%, #0b1630 100%)
    `,
  },
  backgroundVideo: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: 0,
    opacity: 0.3,
    pointerEvents: "none",
    transition: "opacity 0.35s ease, filter 0.35s ease",
  },
  cloud: { position: "fixed", borderRadius: "50%", filter: "blur(40px)", pointerEvents: "none" },
  c1: { width: 700, height: 300, top: -80, left: -100, background: "radial-gradient(ellipse, rgba(80,110,180,0.18) 0%, transparent 70%)" },
  c2: { width: 500, height: 250, top: "30%", right: -80, background: "radial-gradient(ellipse, rgba(60,90,160,0.15) 0%, transparent 70%)" },
  c3: { width: 600, height: 200, bottom: "5%", left: "20%", background: "radial-gradient(ellipse, rgba(40,70,140,0.12) 0%, transparent 70%)" },

  nav: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
    padding: "0 40px", height: 68, display: "flex", alignItems: "center",
    background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)",
  },
  navInner: {
    width: "100%", maxWidth: 1120, margin: "0 auto",
    display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 24, alignItems: "center",
  },
  navLinks: { display: "flex", justifyContent: "center", gap: 32, listStyle: "none", margin: 0, padding: 0 },
  navLink: { color: "rgba(255,255,255,0.65)", fontSize: 13, textDecoration: "none" },
  navActions: { display: "flex", alignItems: "center", gap: 12 },
  btnGhost: {
    background: "none", border: "none", cursor: "pointer",
    color: "rgba(255,255,255,0.65)", fontFamily: "'DM Sans', sans-serif",
    fontSize: 13, display: "flex", alignItems: "center", gap: 8,
  },
  btnNav: {
    padding: "8px 22px", borderRadius: 100, border: "none", cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500, color: "#fff",
    background: "linear-gradient(135deg, #FF2525, #8B1010)",
    boxShadow: "0 0 22px rgba(255,37,37,0.35)",
  },

  page: {
    position: "relative", zIndex: 1,
    minHeight: "100vh", display: "flex", alignItems: "center",
    padding: "80px 40px 40px",
  },
  container: {
    width: "100%", maxWidth: 1100, margin: "0 auto",
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center",
  },

  left: { display: "flex", flexDirection: "column", gap: 28 },
  eyebrow: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "rgba(255,37,37,0.12)", border: "1px solid rgba(255,37,37,0.25)",
    borderRadius: 100, padding: "5px 14px",
    fontSize: 11, fontWeight: 500, letterSpacing: "0.8px", textTransform: "uppercase",
    color: "#ff6b6b", width: "fit-content",
  },
  eyebrowDot: {
    width: 6, height: 6, borderRadius: "50%",
    background: "#FF2525", boxShadow: "0 0 8px #FF2525", display: "inline-block",
  },
  h1: { fontSize: "clamp(28px, 3.5vw, 46px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: -1, marginBottom: -15 },
  em: { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 400, color: "rgba(255,255,255,0.75)" },
  stepByStep: { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 400, color: "#fff" },
  subtitle: { fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, maxWidth: 380, marginBottom: 40 },

  deckOuter: { position: "relative" },
  deckWrap: { position: "relative", height: 220 },

  card: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.14)",
    backdropFilter: "blur(20px) saturate(1.4)",
    WebkitBackdropFilter: "blur(20px) saturate(1.4)",
    borderRadius: 16, padding: 24,
    willChange: "transform, opacity",
    userSelect: "none",
  },
  cardQuote: {
    fontSize: 13, lineHeight: 1.7, color: "rgba(255,255,255,0.82)",
    marginBottom: 16
  },
  quoteMark: { color: "#ffffff", fontSize: 18, fontStyle: "normal" },
  cardAuthor: { display: "flex", alignItems: "center", gap: 10 },
  avatar: {
    width: 36, height: 36, borderRadius: "50%",
    overflow: "hidden",
    background: "rgba(255,255,255,0.08)",
    border: "2px solid rgba(255,255,255,0.14)",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    padding: 4,
  },
  avatarSvg: {
    width: "100%",
    height: "100%",
    display: "block",
  },
  authorName: { fontSize: 13, fontWeight: 600, color: "#fff" },
  authorRole: { fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2 },
  tapHint: {
    marginTop: 10, fontSize: 10, color: "rgba(255,255,255,0.22)",
    textAlign: "right", letterSpacing: "0.3px",
  },

  formGlass: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.13)",
    backdropFilter: "blur(28px) saturate(1.5)",
    WebkitBackdropFilter: "blur(28px) saturate(1.5)",
    borderRadius: 24, padding: "40px 36px",
    boxShadow: "0 24px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
  },
  formTitle: { fontSize: 22, fontWeight: 600, marginBottom: 6, letterSpacing: -0.4 },
  formSub: { fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 28 },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: {
    display: "block", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.6px", textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)", marginBottom: 6,
  },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 12, padding: "13px 16px",
    fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#fff",
    transition: "border-color 0.2s, background 0.2s",
  },
  btnSubmit: {
    width: "100%", padding: 15, borderRadius: 12, border: "none", cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, color: "#fff",
    background: "linear-gradient(135deg, #FF2525 0%, #8B1010 100%)",
    boxShadow: "0 0 30px rgba(255,37,37,0.4)",
    marginTop: 8,
  },
  statusText: {
    marginTop: 12,
    fontSize: 13,
    color: "rgba(255,255,255,0.78)",
    minHeight: 20,
    lineHeight: 1.5,
  },
  resendRow: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 10,
  },
  resendLink: {
    border: "none",
    background: "none",
    color: "#ff6b6b",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    padding: 0,
    textAlign: "left",
    transition: "color 0.2s, text-decoration 0.2s",
  },
  resendNotice: {
    fontSize: 12,
    color: "#9cf7c7",
    lineHeight: 1.4,
  },
  formFooter: { textAlign: "center", marginTop: 16, fontSize: 12, color: "rgba(255,255,255,0.3)" },
  footerLink: { color: "rgba(255,255,255,0.55)" },
  divider: { display: "flex", alignItems: "center", gap: 12, margin: "18px 0" },
  dividerLine: { flex: 1, height: 1, background: "rgba(255,255,255,0.1)" },
  dividerText: { fontSize: 11, color: "rgba(255,255,255,0.3)", whiteSpace: "nowrap" },
  signInLink: { textAlign: "center", fontSize: 13 },
  signInA: { color: "#ff6b6b", textDecoration: "none", fontWeight: 500 },
};